import {
  GCM_NONCE_LEN,
  GCM_TAG_LEN,
  base64UrlLength,
  concatBytes,
  fromBase64Url,
  isJsonObject,
} from './bytes';
import { EnvelopeError, UnverifiedEnvelopeError } from './errors';

import type { EnvelopeHeader, HeaderSigningKey, RawEnvelope, VerifiedEnvelope } from './types';

const SIGNATURE_DOMAIN = concatBytes([
  new TextEncoder().encode('ORSNN-IRM-HEADER-SIG-v1'),
  Uint8Array.of(0),
]);
const HEADER_FIELDS = [
  'format_ver',
  'launcher_ver',
  'alg',
  'download_id',
  'sale_key',
  'file_hash',
  'content_type',
  'plain_len',
  'seg_size',
  'seg_count',
  'sig_kid',
];
const FORMAT_VER = 2;
const SEGMENT_ALG = 'A256GCM-SEG';
const PDF_CONTENT_TYPE = 'application/pdf';
const MIN_SEG_SIZE = 16;
const MAX_SEG_SIZE = 16 * 1024 * 1024;
const SEGMENT_OVERHEAD = GCM_NONCE_LEN + GCM_TAG_LEN;
const FILE_HASH_PATTERN = /^sha256:[0-9a-f]{64}$/;
const GO_JSON_INTEGER = /^-?(?:0|[1-9][0-9]*)$/;
const JSON_NUMBER_CHAR = /[-+.eE0-9]/;

const P256_SCALAR_LEN = 32;
const MAX_P256_DER_SIGNATURE_LEN = 72;
const DER_SEQUENCE_TAG = 0x30;
const DER_INTEGER_TAG = 0x02;

const PROTECT_MAX_SALE_KEY_BYTES = 1024;
const GO_JSON_MAX_BYTES_PER_STRING_BYTE = 6;
const HEADER_BUDGET_FOR_OTHER_FIELDS = 1024;
const MAX_HEADER_BYTES =
  PROTECT_MAX_SALE_KEY_BYTES * GO_JSON_MAX_BYTES_PER_STRING_BYTE + HEADER_BUDGET_FOR_OTHER_FIELDS;

const PROTECT_LAUNCHER_BYTES_CEILING = 500 * 1024 * 1024;
const MAX_CIPHERTEXT_BYTES = (PROTECT_LAUNCHER_BYTES_CEILING / 4) * 3;

// ignoreBOM keeps a byte order mark in the text, so JSON.parse refuses it as Go does.
const UTF8_FATAL_KEEPING_BOM = new TextDecoder('utf-8', { fatal: true, ignoreBOM: true });

interface SignedHeaderFields {
  format_ver: typeof FORMAT_VER;
  launcher_ver: number;
  alg: typeof SEGMENT_ALG;
  download_id: string;
  sale_key: string;
  file_hash: string;
  content_type: typeof PDF_CONTENT_TYPE;
  plain_len: number;
  seg_size: number;
  seg_count: number;
  sig_kid: string;
}

export interface Segment {
  index: number;
  final: boolean;
  start: number;
  end: number;
  plaintextOffset: number;
}

const verifiedEnvelopes = new WeakSet<object>();

export function assertVerified(envelope: VerifiedEnvelope): void {
  if (!verifiedEnvelopes.has(envelope)) throw new UnverifiedEnvelopeError();
}

export async function verifyEnvelope(
  raw: RawEnvelope,
  keys: HeaderSigningKey[],
): Promise<VerifiedEnvelope> {
  if (!(raw.ct instanceof ArrayBuffer)) throw new EnvelopeError('signature');
  if (raw.ct.byteLength > MAX_CIPHERTEXT_BYTES) throw new EnvelopeError('length');
  const { headerBytes, verifiedKids } = await authenticatedHeaderBytes(raw, keys);
  const header = parseSignedHeader(headerBytes, verifiedKids);
  const ct = new Uint8Array(raw.ct);
  if (ct.length !== ciphertextLength(header)) throw new EnvelopeError('length');
  const verified = Object.freeze({
    header: Object.freeze(header),
    headerBytes,
    ct,
  }) as VerifiedEnvelope;
  verifiedEnvelopes.add(verified);
  return verified;
}

async function authenticatedHeaderBytes(
  raw: RawEnvelope,
  keys: HeaderSigningKey[],
): Promise<{ headerBytes: Uint8Array; verifiedKids: string[] }> {
  if (
    typeof raw.header !== 'string' ||
    typeof raw.sig !== 'string' ||
    raw.header.length > base64UrlLength(MAX_HEADER_BYTES) ||
    raw.sig.length > base64UrlLength(MAX_P256_DER_SIGNATURE_LEN)
  ) {
    throw new EnvelopeError('signature');
  }
  const headerBytes = fromBase64Url(raw.header);
  const derSignature = fromBase64Url(raw.sig);
  const rawSignature = derSignature && derToRawSignature(derSignature);
  if (!headerBytes || !rawSignature) throw new EnvelopeError('signature');

  const verifiedKids = await kidsThatVerify(keys, rawSignature, headerBytes);
  if (verifiedKids.length === 0) throw new EnvelopeError('signature');
  return { headerBytes, verifiedKids };
}

async function kidsThatVerify(
  keys: HeaderSigningKey[],
  rawSignature: Uint8Array,
  headerBytes: Uint8Array,
): Promise<string[]> {
  const signed = concatBytes([SIGNATURE_DOMAIN, headerBytes]);
  const kids: string[] = [];
  for (const { kid, publicKey } of await importableHeaderSigningKeys(keys)) {
    const verified = await crypto.subtle.verify(
      { name: 'ECDSA', hash: 'SHA-256' },
      publicKey,
      rawSignature,
      signed,
    );
    if (verified) kids.push(kid);
  }
  return kids;
}

interface ImportedSigningKey {
  kid: string;
  publicKey: CryptoKey;
}

// The user is told the file isn't a genuine copy, so the console is where whoever deployed the
// page learns that the build config is wrong.
async function importableHeaderSigningKeys(
  keys: HeaderSigningKey[],
): Promise<ImportedSigningKey[]> {
  const importable: ImportedSigningKey[] = [];
  for (const key of keys) {
    const publicKey = await importHeaderSigningKey(key);
    if (publicKey) importable.push({ kid: key.kid, publicKey });
  }
  if (importable.length === 0) {
    console.error(
      'IRM headerSigningKeys in the build config has no key this browser can import: no file can be verified',
    );
  }
  return importable;
}

async function importHeaderSigningKey({ kid, jwk }: HeaderSigningKey): Promise<CryptoKey | null> {
  try {
    return await crypto.subtle.importKey(
      'jwk',
      jwk,
      { name: 'ECDSA', namedCurve: 'P-256' },
      false,
      ['verify'],
    );
  } catch {
    console.warn(`IRM header signing key ${kid} could not be imported and was skipped`);
    return null;
  }
}

function derToRawSignature(der: Uint8Array): Uint8Array | null {
  if (der.length < 2 || der[0] !== DER_SEQUENCE_TAG || der[1] !== der.length - 2) return null;
  const r = readDerScalar(der, 2);
  const s = r && readDerScalar(der, r.end);
  if (!r || !s || s.end !== der.length) return null;
  const raw = new Uint8Array(2 * P256_SCALAR_LEN);
  raw.set(r.magnitude, P256_SCALAR_LEN - r.magnitude.length);
  raw.set(s.magnitude, 2 * P256_SCALAR_LEN - s.magnitude.length);
  return raw;
}

// Minimal non-negative INTEGERs only, as Go's ecdsa.VerifyASN1 requires.
function readDerScalar(
  der: Uint8Array,
  offset: number,
): { magnitude: Uint8Array; end: number } | null {
  if (offset + 2 > der.length || der[offset] !== DER_INTEGER_TAG) return null;
  const start = offset + 2;
  const end = start + der[offset + 1];
  if (end === start || end > der.length) return null;
  const value = der.subarray(start, end);
  const negative = (value[0] & 0x80) !== 0;
  const needlessLeadingZero = value.length > 1 && value[0] === 0 && (value[1] & 0x80) === 0;
  if (negative || needlessLeadingZero) return null;
  const magnitude = value.length > 1 && value[0] === 0 ? value.subarray(1) : value;
  return magnitude.length > P256_SCALAR_LEN ? null : { magnitude, end };
}

function parseSignedHeader(headerBytes: Uint8Array, verifiedKids: string[]): EnvelopeHeader {
  const fields = decodeHeaderJson(headerBytes);
  if (!followsHeaderRules(fields) || !verifiedKids.includes(fields.sig_kid)) {
    throw new EnvelopeError('header');
  }
  return toEnvelopeHeader(fields);
}

function decodeHeaderJson(headerBytes: Uint8Array): Record<string, unknown> {
  let text: string;
  let parsed: unknown;
  try {
    text = UTF8_FATAL_KEEPING_BOM.decode(headerBytes);
    parsed = JSON.parse(text);
  } catch {
    throw new EnvelopeError('header');
  }
  if (!isJsonObject(parsed) || !hasOnlyGoIntegerNumbers(text)) throw new EnvelopeError('header');
  return parsed;
}

function followsHeaderRules(
  fields: Record<string, unknown>,
): fields is Record<string, unknown> & SignedHeaderFields {
  return (
    hasExactlyHeaderFields(fields) &&
    fields.format_ver === FORMAT_VER &&
    fields.alg === SEGMENT_ALG &&
    isNonNegativeInteger(fields.launcher_ver) &&
    isNonNegativeInteger(fields.plain_len) &&
    isNonNegativeInteger(fields.seg_size) &&
    fields.seg_size >= MIN_SEG_SIZE &&
    fields.seg_size <= MAX_SEG_SIZE &&
    fields.seg_count === expectedSegCount(fields.plain_len, fields.seg_size) &&
    isNonEmptyString(fields.download_id) &&
    isNonEmptyString(fields.sale_key) &&
    isNonEmptyString(fields.sig_kid) &&
    fields.content_type === PDF_CONTENT_TYPE &&
    typeof fields.file_hash === 'string' &&
    FILE_HASH_PATTERN.test(fields.file_hash)
  );
}

function hasExactlyHeaderFields(fields: Record<string, unknown>): boolean {
  const names = Object.keys(fields);
  return (
    names.length === HEADER_FIELDS.length && HEADER_FIELDS.every((name) => names.includes(name))
  );
}

function toEnvelopeHeader(fields: SignedHeaderFields): EnvelopeHeader {
  return {
    formatVer: fields.format_ver,
    launcherVer: fields.launcher_ver,
    alg: fields.alg,
    downloadId: fields.download_id,
    saleKey: fields.sale_key,
    fileHash: fields.file_hash,
    contentType: fields.content_type,
    plainLen: fields.plain_len,
    segSize: fields.seg_size,
    segCount: fields.seg_count,
    sigKid: fields.sig_kid,
  };
}

export function hasOnlyGoIntegerNumbers(json: string): boolean {
  let index = 0;
  while (index < json.length) {
    const char = json[index];
    if (char === '"') {
      index = indexAfterString(json, index);
    } else if (char === '-' || (char >= '0' && char <= '9')) {
      const start = index;
      while (index < json.length && JSON_NUMBER_CHAR.test(json[index])) index += 1;
      if (!GO_JSON_INTEGER.test(json.slice(start, index))) return false;
    } else {
      index += 1;
    }
  }
  return true;
}

function indexAfterString(json: string, openingQuote: number): number {
  let index = openingQuote + 1;
  while (index < json.length && json[index] !== '"') index += json[index] === '\\' ? 2 : 1;
  return index + 1;
}

export function* segmentsOf(header: EnvelopeHeader, ct: Uint8Array): Generator<Segment> {
  const fullSegmentLen = header.segSize + SEGMENT_OVERHEAD;
  for (let index = 0; index < header.segCount; index++) {
    const final = index === header.segCount - 1;
    const start = index * fullSegmentLen;
    const end = final ? ct.length : start + fullSegmentLen;
    // ct is a view of the launcher's buffer, which can be resized after verifyEnvelope.
    if (end - start < SEGMENT_OVERHEAD || end - start > fullSegmentLen || end > ct.length) {
      throw new EnvelopeError('length', index);
    }
    yield { index, final, start, end, plaintextOffset: index * header.segSize };
  }
}

function ciphertextLength(header: EnvelopeHeader): number {
  return header.plainLen + header.segCount * SEGMENT_OVERHEAD;
}

function expectedSegCount(plainLen: number, segSize: number): number {
  return plainLen === 0 ? 1 : Math.ceil(plainLen / segSize);
}

function isNonNegativeInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value !== '';
}
